import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useWhatsMissing, type Difficulty, type GameMode } from './useWhatsMissing'
import { useSessionMode } from '../../professional/SessionModeContext'
import { DIFFICULTY_CONFIG, type ShapeType, type MissingItem } from './WhatsMissingData'
import { useTimer } from '../useTimer'
import { formatGameTime } from '../utils'
import { useMobile } from '../../hooks/useMobile'
import GameSetupScreen from '../GameSetupScreen'
import type { DifficultyOption } from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import GamePlayHeader from '../GamePlayHeader'
import { ss } from '../gameSetupStyles'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { COLORS, SHADOWS, RADIUS, FONT } from '../../styles/tokens'

// ── Shape renderer ─────────────────────────────────────────────────────────────

function poly(sides: number, r = 42): string {
  return Array.from({ length: sides }, (_, i) => {
    const a = (i * (2 * Math.PI / sides)) - Math.PI / 2
    return `${(50 + r * Math.cos(a)).toFixed(1)},${(50 + r * Math.sin(a)).toFixed(1)}`
  }).join(' ')
}

function starPts(): string {
  return Array.from({ length: 10 }, (_, i) => {
    const a = (i * Math.PI / 5) - Math.PI / 2
    const r = i % 2 === 0 ? 42 : 18
    return `${(50 + r * Math.cos(a)).toFixed(1)},${(50 + r * Math.sin(a)).toFixed(1)}`
  }).join(' ')
}

// SVG uses percentage dimensions so the grid cell dictates the rendered size
function ShapeIcon({ shape, color }: { shape: ShapeType; color: string }) {
  return (
    <svg width="82%" height="82%" viewBox="0 0 100 100" aria-hidden>
      {shape === 'circle'   && <circle cx="50" cy="50" r="42" fill={color} />}
      {shape === 'square'   && <rect x="8" y="8" width="84" height="84" fill={color} rx="4" />}
      {shape === 'triangle' && <polygon points={poly(3)} fill={color} />}
      {shape === 'pentagon' && <polygon points={poly(5)} fill={color} />}
      {shape === 'hexagon'  && <polygon points={poly(6)} fill={color} />}
      {shape === 'star'     && <polygon points={starPts()} fill={color} />}
    </svg>
  )
}

// ── Item cell ─────────────────────────────────────────────────────────────────

function ItemCell({
  item, isSelected, isCorrect, isWrongFlash, readOnly, onTap,
}: {
  item:         MissingItem
  isSelected:   boolean
  isCorrect:    boolean
  isWrongFlash: boolean
  readOnly:     boolean
  onTap:        () => void
}) {
  const bg     = isSelected ? (isCorrect ? COLORS.greenSurface : COLORS.errorBg) : 'transparent'
  const border = isSelected
    ? `2.5px solid ${isCorrect ? COLORS.greenLight : COLORS.errorText}`
    : '2.5px solid transparent'

  return (
    <button
      onClick={readOnly ? undefined : onTap}
      className={isWrongFlash ? 'wm-shake' : undefined}
      style={{
        // container-type enables cqmin units so emoji scales with cell size
        containerType:  'size' as React.CSSProperties['position'],
        width:          '100%',
        height:         '100%',
        background:     bg,
        border,
        borderRadius:   8,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        cursor:         readOnly ? 'default' : 'pointer',
        opacity:        readOnly ? 0.55 : 1,
        padding:        0,
        transition:     'background 0.15s, border-color 0.15s',
        boxShadow:      isSelected ? SHADOWS.subtle : 'none',
        minWidth:       0,
        minHeight:      0,
      }}
      aria-pressed={readOnly ? undefined : isSelected}
      aria-label={item.emoji ?? `${item.shape} ${item.color}`}
    >
      {item.mode === 'emoji'
        ? <span style={{ fontSize: '65cqmin' as React.CSSProperties['fontSize'], lineHeight: 1 }}>{item.emoji}</span>
        : <ShapeIcon shape={item.shape!} color={item.color!} />}
    </button>
  )
}

// ── Setup screen ──────────────────────────────────────────────────────────────

function SetupScreen({
  mode, onModeChange, difficulty, onDifficultyChange, onBack, onStart,
}: {
  mode:               GameMode
  onModeChange:       (m: GameMode) => void
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onBack:             () => void
  onStart:            () => void
}) {
  const { t } = useTranslation()
  const wm = t.whatsMissing

  const difficulties: DifficultyOption[] = [
    { id: 'easy',   label: wm.difficulty.easy.label,   hint: wm.difficulty.easy.hint   },
    { id: 'medium', label: wm.difficulty.medium.label, hint: wm.difficulty.medium.hint },
    { id: 'hard',   label: wm.difficulty.hard.label,   hint: wm.difficulty.hard.hint   },
  ]

  const MODES: { id: GameMode; emoji: string; label: string }[] = [
    { id: 'emoji',  emoji: '🎭', label: wm.modes.emoji.label  },
    { id: 'shapes', emoji: '⬡',  label: wm.modes.shapes.label },
  ]

  const preview = (
    <div style={s.previewWrap}>
      <div style={s.previewPanel}>
        {['🍎','🐶','🎸','🌮','🎈'].map(e => (
          <span key={e} style={s.previewEmoji}>{e}</span>
        ))}
      </div>
      <span style={s.previewVs}>≠</span>
      <div style={s.previewPanel}>
        {['🍎','🐶','🎸'].map(e => (
          <span key={e} style={s.previewEmoji}>{e}</span>
        ))}
      </div>
    </div>
  )

  return (
    <GameSetupScreen
      title={wm.title}
      subtitle={wm.subtitle}
      backLabel={wm.backToDashboard}
      onBack={onBack}
      difficulties={difficulties}
      selectedDifficulty={difficulty}
      onDifficultyChange={v => onDifficultyChange(v as Difficulty)}
      chooseDifficultyLabel={wm.chooseDifficultyTitle}
      preview={preview}
      startLabel={wm.startBtn}
      onStart={onStart}
    >
      {/* Mode selector — theme-card style */}
      <div>
        <h2 style={ss.optionLabel}>{wm.chooseModeTitle}</h2>
        <div style={ss.themeRow}>
          {MODES.map(m => (
            <button
              key={m.id}
              onClick={() => onModeChange(m.id)}
              style={{ ...ss.themeBtn, ...(mode === m.id ? ss.themeBtnActive : {}) }}
            >
              <span style={ss.themeEmoji}>{m.emoji}</span>
              <span style={ss.themeLabel}>{m.label}</span>
            </button>
          ))}
        </div>
      </div>
    </GameSetupScreen>
  )
}

// ── Play screen ───────────────────────────────────────────────────────────────

function PlayScreen({
  hook, difficulty, onBack,
}: {
  hook:       ReturnType<typeof useWhatsMissing>
  difficulty: Difficulty
  onBack:     () => void
}) {
  const { t } = useTranslation()
  const wm    = t.whatsMissing
  const isMobile = useMobile()

  const { state, tapItem } = hook
  const { leftSet, rightSet, correctIds, selectedIds, wrongFlashId } = state

  const timer = useTimer(state.startTime, state.endTime)
  const cfg   = DIFFICULTY_CONFIG[difficulty]

  const correctSelected = selectedIds.filter(id => correctIds.includes(id)).length
  const remaining       = correctIds.length - correctSelected
  const progress        = correctIds.length > 0 ? (correctSelected / correctIds.length) * 100 : 0
  const isEasy          = !cfg.bidirectional

  function renderPanel(items: MissingItem[], label: string, hint: string | null, readOnly: boolean) {
    const rows = Math.ceil(items.length / cfg.columns)
    return (
      <div style={s.panel}>
        <div style={s.panelHeader}>
          <span style={s.panelLetter}>{label}</span>
          {hint && <span style={s.panelHint}>{hint}</span>}
        </div>
        <div style={{
          flex:                '1',
          display:             'grid',
          gridTemplateColumns: `repeat(${cfg.columns}, 1fr)`,
          gridTemplateRows:    `repeat(${rows}, 1fr)`,
          gap:                 3,
          padding:             2,
          overflow:            'hidden',
          minHeight:           0,
        }}>
          {items.map(item => {
            const isSelected   = selectedIds.includes(item.id)
            const isCorrect    = correctIds.includes(item.id)
            const isWrongFlash = item.id === wrongFlashId
            return (
              <ItemCell
                key={item.id}
                item={item}
                isSelected={isSelected}
                isCorrect={isCorrect}
                isWrongFlash={isWrongFlash}
                readOnly={readOnly}
                onTap={() => tapItem(item.id)}
              />
            )
          })}
        </div>
      </div>
    )
  }

  const instruction = isEasy
    ? wm.instructionEasy.replace('{n}', String(remaining))
    : wm.instructionBidi.replace('{n}', String(remaining))

  const panelsDirection: React.CSSProperties = isMobile
    ? { flexDirection: 'column' }
    : { flexDirection: 'row' }

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={wm.title}
        stats={[
          { label: wm.moves, value: String(state.moves) },
          { label: wm.time,  value: timer },
        ]}
        progress={progress}
        progressMax={100}
        progressNow={correctSelected}
        onBack={onBack}
        backLabel={wm.backToDashboard}
      />

      <div style={s.playBody}>
        <p style={s.instruction}>{instruction}</p>

        <div style={{ ...s.panelsRow, ...panelsDirection }}>
          {renderPanel(leftSet,  'A', isEasy ? wm.panelSelect : null, false)}

          <div style={{ ...s.divider, ...(isMobile ? s.dividerH : s.dividerV) }} aria-hidden>≠</div>

          {renderPanel(rightSet, 'B', isEasy ? wm.panelRef : null, isEasy)}
        </div>
      </div>
    </div>
  )
}

// ── Main game ─────────────────────────────────────────────────────────────────

type GameScreen = 'setup' | 'playing' | 'complete'

export default function WhatsMissingGame({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  const wm = t.whatsMissing
  const sessionMode = useSessionMode()

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : 'setup')
  const [mode,       setMode]       = useState<GameMode>(
    (sessionMode?.config.mode as GameMode) ?? 'emoji',
  )
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? 'easy',
  )

  const hook = useWhatsMissing(difficulty, mode)
  const { state, resetGame } = hook

  const pairsTotal   = state.correctIds.length
  const pairsMatched = state.selectedIds.filter(id => state.correctIds.includes(id)).length

  const { sessionId } = useGameSession({
    activitySlug: 'whats_missing',
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  state.gameComplete,
    activitySlug:  'whats_missing',
    difficulty,
    moves:         state.moves,
    startTime:     state.startTime,
    endTime:       state.endTime,
    pairsTotal,
    pairsMatched,
    mode,
    sessionId,
    onCompleted:   () => setScreen('complete'),
  })

  function startGame() {
    resetGame()
    setScreen('playing')
  }

  return (
    <>
      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          onModeChange={setMode}
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onBack={onBack}
          onStart={startGame}
        />
      )}

      {screen === 'playing' && (
        <>
          <PlayScreen
            hook={hook}
            difficulty={difficulty}
            onBack={() => setScreen('setup')}
          />
          {saveError && (
            <SaveErrorBanner
              message={t.common.saveError}
              dismissLabel={t.common.dismissError}
              onDismiss={clearSaveError}
            />
          )}
        </>
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🔎"
          title={wm.complete.title}
          subtitle={wm.complete.subtitle}
          stats={[
            { label: wm.complete.movesLabel, value: String(state.moves) },
            { label: wm.complete.timeLabel,  value: formatGameTime(state.startTime, state.endTime) },
            { label: wm.complete.levelLabel, value: wm.difficulty[difficulty].label },
            { label: wm.complete.foundLabel, value: `${pairsTotal}/${pairsTotal}` },
          ]}
          playAgainLabel={wm.complete.playAgain}
          changeLevelLabel={wm.complete.changeLevel}
          backLabel={wm.complete.backToDashboard}
          onPlayAgain={startGame}
          onChangeLevel={() => setScreen('setup')}
          onBack={onBack}
        />
      )}
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  playRoot: {
    height:        '100dvh',
    background:    COLORS.bgPage,
    display:       'flex',
    flexDirection: 'column',
    overflow:      'hidden',
  },
  playBody: {
    flex:          1,
    padding:       '4px 4px 4px',
    display:       'flex',
    flexDirection: 'column',
    gap:           4,
    overflow:      'hidden',
    minHeight:     0,
  },
  instruction: {
    textAlign:  'center',
    fontStyle:  'italic',
    color:      COLORS.textBody,
    fontSize:   '1.125rem',
    margin:     0,
    padding:    '0 8px',
    lineHeight: 1.3,
    flexShrink: 0,
  },
  panelsRow: {
    flex:           1,
    display:        'flex',
    gap:            8,
    overflow:       'hidden',
    minHeight:      0,
  },
  panel: {
    flex:           1,
    background:     COLORS.bgCard,
    borderRadius:   RADIUS.lg,
    boxShadow:      SHADOWS.card,
    padding:        4,
    display:        'flex',
    flexDirection:  'column',
    overflow:       'hidden',
    minHeight:      0,
    minWidth:       0,
  },
  panelHeader: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            2,
    flexShrink:     0,
    marginBottom:   4,
  },
  panelLetter: {
    fontSize:   '1.25rem',
    fontWeight: 700,
    color:      COLORS.greenDark,
    lineHeight: 1,
  },
  panelHint: {
    fontSize:  FONT.size.base,
    color:     COLORS.textSecondary,
    textAlign: 'center',
  },
  divider: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       '1.5rem',
    fontWeight:     700,
    color:          COLORS.textMuted,
    flexShrink:     0,
  },
  dividerV: {
    flexDirection: 'column',
    width:         24,
  },
  dividerH: {
    flexDirection: 'row',
    height:        24,
  },
  // Setup preview
  previewWrap: {
    display:        'flex',
    flexDirection:  'row',
    gap:            12,
    alignItems:     'center',
    justifyContent: 'center',
    background:     COLORS.bgCard,
    borderRadius:   RADIUS.lg,
    padding:        '12px 16px',
    boxShadow:      SHADOWS.subtle,
  },
  previewPanel: {
    display:        'flex',
    flexWrap:       'wrap',
    gap:            4,
    maxWidth:       100,
    justifyContent: 'center',
  },
  previewEmoji: {
    fontSize: '1.5rem',
  },
  previewVs: {
    fontSize:   '1.75rem',
    fontWeight: 700,
    color:      COLORS.textMuted,
  },
}
